import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

/**
 * IdleWelcome — the Projector's pre-game state.
 * Cinematic particles + hotel logo + IC Bingo mark + welcome text + hotel name.
 */
export default function IdleWelcome({ hotel }) {
  const bg = hotel?.welcomeBackground || {};
  const useImage = bg.type === "image" && bg.assetUrl;
  return (
    <div
      data-testid="projector-idle-welcome"
      className="fixed inset-0 w-screen h-screen overflow-hidden"
      style={{ background: "var(--ic-bg-darkest)" }}
    >
      {useImage ? (
        <ImageBackgroundLayer src={bg.assetUrl} />
      ) : (
        <AmbientLayer />
      )}

      <div className="relative w-full h-full flex flex-col items-center justify-center px-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="flex flex-col items-center gap-6"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0.9 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 3.2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
            className="rounded-2xl flex items-center justify-center border overflow-hidden"
            style={{
              width: hotel?.logoUrl ? "clamp(9rem, 14vw, 15rem)" : "6rem",
              height: hotel?.logoUrl ? "clamp(9rem, 14vw, 15rem)" : "6rem",
              background: hotel?.logoUrl
                ? "transparent"
                : "var(--ic-bg-elevated)",
              borderColor: hotel?.logoUrl
                ? "transparent"
                : "var(--ic-border-subtle)",
              boxShadow: "0 0 80px var(--ic-primary-glow)",
            }}
          >
            {hotel?.logoUrl ? (
              <img
                src={hotel.logoUrl}
                alt={hotel.name}
                className="max-w-[92%] max-h-[92%] object-contain"
                style={{
                  filter: "drop-shadow(0 0 24px var(--ic-primary-glow))",
                }}
              />
            ) : (
              <Sparkles
                className="w-12 h-12"
                style={{ color: "var(--ic-primary)" }}
                strokeWidth={2}
              />
            )}
          </motion.div>
          <div className="ic-font-display leading-none">
            <div
              className="text-2xl md:text-3xl tracking-[0.35em] uppercase"
              style={{ color: "var(--ic-text-muted)" }}
            >
              {hotel?.name || "Your Hotel"}
            </div>
          </div>
        </motion.div>

        <motion.h1
          data-testid="projector-welcome-title"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.4, ease: "easeOut" }}
          className="ic-font-display font-bold mt-14 tracking-tight leading-[0.95]"
          style={{
            fontSize: "clamp(3rem, 9vw, 12rem)",
            color: "var(--ic-text-primary)",
            textShadow: "0 0 60px var(--ic-primary-glow)",
          }}
        >
          {hotel?.welcomeMessage || "Welcome to Bingo Night"}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6, delay: 0.9 }}
          className="mt-8 text-2xl tracking-[0.35em] uppercase"
          style={{ color: "var(--ic-text-secondary)" }}
        >
          IC Bingo · Show Console
        </motion.p>
      </div>

      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[color:var(--ic-text-muted)]">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: "var(--ic-active)" }}
        />
        Ready
      </div>
    </div>
  );
}

function AmbientLayer() {
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(1400px 800px at 50% 40%, var(--ic-primary-glow), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 700px at 20% 90%, rgba(59,130,246,0.12), transparent 60%), radial-gradient(1000px 600px at 90% 10%, rgba(239,68,68,0.08), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <FloatingParticles />
    </>
  );
}

function FloatingParticles() {
  const particles = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const size = 2 + ((i * 7) % 4);
        const left = (i * 43) % 100;
        const delay = (i * 0.4) % 6;
        const duration = 8 + ((i * 3) % 10);
        return (
          <motion.span
            key={i}
            initial={{ y: "110vh", opacity: 0 }}
            animate={{ y: "-10vh", opacity: [0, 1, 1, 0] }}
            transition={{
              duration,
              delay,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              width: size,
              height: size,
              background: "var(--ic-primary)",
              boxShadow: "0 0 12px var(--ic-primary-glow)",
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * ImageBackgroundLayer — full-bleed hotel background image with a soft
 * dark scrim so the welcome text stays readable regardless of the photo.
 */
function ImageBackgroundLayer({ src }) {
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.75) 100%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(1400px 800px at 50% 40%, var(--ic-primary-glow), transparent 60%)",
        }}
      />
    </>
  );
}
